import type { H3Event } from 'h3'
import { defu } from 'defu'
import type { ServiceOptions } from '../types'
import { splitSetCookieString, parseSetCookie, serialize as serializeCookie, parse as parseCookies } from 'cookie-es'
import type { useRequestHeaders } from '#imports'

export interface BuildApiRequestHeadersOptions {
    /**
     * @description The H3 request event
     */
    event: H3Event
    /**
     * @description A Record matching the return type of useRequestHeaders
     */
    requestHeaders: ReturnType<typeof useRequestHeaders>
    /**
     * @description A Record of extra headers to add besides the dynamically built headers
     */
    extraHeaders?: Record<string, any>
    /**
     * @description An optional service config
     */
    service?: ServiceOptions
}

const IGNORE_PROXY_HEADERS = [
    'cf-connecting-ip',
    'cf-ray',
    'connection',
    'content-length',
    'content-md5',
    'content-type',
    'host',
    'x-forwarded-host',
    'x-forwarded-port',
    'x-forwarded-proto',
] as const

/**
 * @title buildApiRequestHeaders
 * @description
 * Generates the Header object for API requests when using `useApi` or derivative functions.
 * It manages cookies to ensure consistency when making multiple API calls on the serverside.
 *
 * @example
 *
 * ```ts
 * const api = useApi()
 *
 * // Server: Make an API request, which returns cookie `x` with a unique value
 * const responseA = await api("/url-that-returns-cookie-x")
 * // Server: Make subsequent request to an endpoint that also returns cookie `x` with a new unique value, unless an existing cookie `x` is included in the request
 * const responseB = await api("/url-that-also-returns-cookie-x")
 *
 * // Because we are using useApi here, the cookie `x` we get return in responseA will be automatically included in the subsequent request for responseB, so responseB don't force a new value.
 * // Usually the browser manages this in SPA, but in Nuxt we need to manage it manually while we are on the server.
 * ```
 * @returns A Headers object
 */
export const buildApiRequestHeaders = (options: BuildApiRequestHeadersOptions): Headers => {
    const { event, requestHeaders, extraHeaders = {}, service } = options

    const rawHeaders: Record<string, string> = defu(extraHeaders, {
        ...requestHeaders,
        'x-requested-with': 'XMLHttpRequest',
        'accept': 'application/json',
    })

    if (import.meta.server) {
        // Set-Cookie headers already added to current Node Response.
        let rawResponseCookies: string | string[] = event.node.res.getHeader('set-cookie') as string | string[]

        if (Array.isArray(rawResponseCookies)) {
            rawResponseCookies = rawResponseCookies.join(';')
        }

        // Cookies on the request from the browser
        const requestCookies = rawHeaders.cookie ? parseCookies(event.node.req.headers?.['cookie'] || '') : {}
        // Parsed cookies from Node Response
        const responseCookies = splitSetCookieString(rawResponseCookies)

        if (responseCookies.length) {
            // Merge request and response cookies
            const presentCookies = new Set<string>()
            const mergedCookies = []

            // Response cookies is more important than cookies from the browser, since they are newer e.g. an API/Service decided to change the value or similar
            for (const responseCookieString of responseCookies) {
                const responseCookie = parseSetCookie(responseCookieString)

                mergedCookies.push(
                    serializeCookie(responseCookie.name, responseCookie.value, {
                        domain: responseCookie.domain,
                        expires: responseCookie.expires,
                        httpOnly: responseCookie.httpOnly,
                        maxAge: responseCookie.maxAge,
                        path: responseCookie.path,
                        sameSite: responseCookie.sameSite,
                        secure: responseCookie.secure,
                    })
                )

                presentCookies.add(responseCookie.name)
            }

            // Iterate through browser cookies
            for (const [name, value] of Object.entries(requestCookies)) {
                // Cookie is already set in response cookies, skip it...
                if (presentCookies.has(name)) {
                    continue
                }

                mergedCookies.push(`${name}=${value}`)
                presentCookies.add(name)
            }

            // Set merged cookies as new cookie header on rawHeaders. This will make sure that the API request contains the merged result of cookies from previous serverside API calls and the cookies from the browser from when the request was made initially.
            rawHeaders.cookie = mergedCookies.join('; ')
        }
    }

    const headers = new Headers(rawHeaders)

    // Remove proxy headers
    for (const header of IGNORE_PROXY_HEADERS) {
        if (import.meta.server) {
            if (header === 'cf-connecting-ip' && service?.server?.forward?.cfConnectingIp) continue
        }

        headers.delete(header)
    }

    return headers
}
